import { MNSproCloudApi } from '..';
import {
  ClassBasicResponse,
  ClassReportRequest,
  CourseBasicResponse,
  CourseReportRequest,
  StudentReportRequest,
  User,
} from '../api/v2';
import { ReportDownload, parseReportFileName, toPdfBlob } from './reportDownload';

const DEFAULT_FILE_NAME = 'report.pdf';

export class ReportsService {
  // Todo: Replace with dedicated endpoints when available
  static async getCourses(
    authToken: string,
    organizationId: string,
  ): Promise<CourseBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicCourses(organizationId);
    return result.data.value;
  }

  // Todo: Replace with dedicated endpoints when available
  static async getClasses(
    authToken: string,
    organizationId: string,
  ): Promise<ClassBasicResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);
    const result = await api.getAllBasicClasses(organizationId);
    return result.data.value;
  }

  /**
   * Fetches the list of students for the reporting view from the
   * StudentDossiers source (`GET /api/v2/{organizationId}/users/dossiers`).
   * Returns the `User` shape; existing label/sorting helpers consume the same
   * person fields (firstName, lastName, displayName).
   */
  static async getStudents(
    authToken: string,
    organizationId: string,
  ): Promise<User[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UsersApiFactory(config);
    const result = await api.getStudentsFromDossiers(organizationId);
    return result.data;
  }

  /**
   * Generates the student report ("Schülerbericht") as a PDF
   * (`POST /api/v2/{organizationId}/reports/student`).
   *
   * @static
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @param {string} organizationId
   * @param {StudentReportRequest} request student, period and time zone
   * @return {*} {Promise<ReportDownload>} the PDF blob and its file name
   * @memberof ReportsService
   */
  static async generateStudentReport(
    authToken: string,
    organizationId: string,
    request: StudentReportRequest,
  ): Promise<ReportDownload> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ReportsApiFactory(config);

    const response = await api.generateStudentReport(organizationId, request, {
      responseType: 'blob',
    });

    return {
      blob: toPdfBlob(response.data),
      fileName: parseReportFileName(response.headers?.['content-disposition'], DEFAULT_FILE_NAME),
    };
  }

  /**
   * Generates the course report ("Kursbericht") as a PDF
   * (`POST /api/v2/{organizationId}/reports/course`).
   *
   * @static
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @param {string} organizationId
   * @param {CourseReportRequest} request course, period, time zone and sorting
   * @return {*} {Promise<ReportDownload>} the PDF blob and its file name
   * @memberof ReportsService
   */
  static async generateCourseReport(
    authToken: string,
    organizationId: string,
    request: CourseReportRequest,
  ): Promise<ReportDownload> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ReportsApiFactory(config);

    const response = await api.generateCourseReport(organizationId, request, {
      responseType: 'blob',
    });

    return {
      blob: toPdfBlob(response.data),
      fileName: parseReportFileName(response.headers?.['content-disposition'], DEFAULT_FILE_NAME),
    };
  }

  /**
   * Generates the class report ("Klassenbericht") as a PDF
   * (`POST /api/v2/{organizationId}/reports/class`).
   *
   * @static
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @param {string} organizationId
   * @param {ClassReportRequest} request class, period, time zone and sorting
   * @return {*} {Promise<ReportDownload>} the PDF blob and its file name
   * @memberof ReportsService
   */
  static async generateClassReport(
    authToken: string,
    organizationId: string,
    request: ClassReportRequest,
  ): Promise<ReportDownload> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ReportsApiFactory(config);

    const response = await api.generateClassReport(organizationId, request, {
      responseType: 'blob',
    });

    return {
      blob: toPdfBlob(response.data),
      fileName: parseReportFileName(response.headers?.['content-disposition'], DEFAULT_FILE_NAME),
    };
  }
}
